import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

const featuredMovies = movies.filter((m) => m.featured);

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [isTransitioning],
  );

  const next = useCallback(() => {
    goTo((current + 1) % featuredMovies.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + featuredMovies.length) % featuredMovies.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const movie = featuredMovies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="推荐影片轮播">
      {featuredMovies.map((m, index) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-500 ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={m.image}
            alt={m.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-center">
        <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
          <div className="max-w-2xl">
            <div className="flex flex-wrap gap-2 mb-4">
              {movie.isNew && (
                <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                  最新上线
                </span>
              )}
              {movie.isHot && (
                <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                  热播推荐
                </span>
              )}
              <span className="px-3 py-1 rounded-full bg-white/20 text-background-50 text-xs font-medium backdrop-blur-sm whitespace-nowrap">
                {movie.subcategory}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-3 font-heading leading-tight">
              {movie.title}
            </h1>
            <p className="text-sm md:text-base text-background-200/90 mb-3 leading-relaxed line-clamp-2 md:line-clamp-3">
              {movie.description}
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-background-200/80">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                {movie.rating}
              </span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.duration}</span>
            </div>
            <Link
              to={`/article/${movie.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即观看
            </Link>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-background-50 transition-colors cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-background-50 transition-colors cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {featuredMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === current ? 'w-8 bg-primary-500' : 'w-2 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}